import React, { useEffect, useRef, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import { Favorite, FavoriteBorder, Share } from '@mui/icons-material';
import { Snackbar, Alert } from '@mui/material';
import L from 'leaflet';
import { Event, Location } from '../types';
import { useFavorites } from '../contexts/FavoritesContext';
import 'leaflet/dist/leaflet.css';

// Custom tooltip styles and mobile touch fixes
const tooltipStyles = `
  .custom-tooltip.leaflet-tooltip {
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid #6366f1;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(8px);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }
  .custom-tooltip.leaflet-tooltip::before {
    border-top-color: #6366f1;
  }
  .map-container {
    touch-action: manipulation !important;
    pointer-events: auto !important;
  }
  .leaflet-container {
    touch-action: manipulation !important;
    pointer-events: auto !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = tooltipStyles;
  document.head.appendChild(styleSheet);
}

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom hotel icon (house) - larger and with background circle
const hotelIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="#6366f1" stroke="#ffffff" stroke-width="3"/>
      <path d="M16 28v-8h8v8h6v-10h4L20 10 6 20h4v8z" fill="white"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// Custom event icon (star) - larger and with background circle
const eventIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="#f59e0b" stroke="#ffffff" stroke-width="3"/>
      <path d="M20 26.27L26.18 30l-1.64-7.03L30 18.24l-7.19-.61L20 11 17.19 17.63 10 18.24l5.46 4.73L13.82 30z" fill="white"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// Custom activities icon (ticket shape matching Material-UI LocalActivity) - larger and with background circle
const activitiesIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="#0ea5e9" stroke="#ffffff" stroke-width="3"/>
      <path d="M27 13H13c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V15c0-1.1-.9-2-2-2zm-7 2l1.15 2.5L24 18.16l-2.16 2.16L22.35 23L20 21.8L17.65 23l.51-2.68L16 18.16l2.85-.66L20 15z" fill="white"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// Large versions for highlighted events
const hotelIconLarge = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="55" height="55" viewBox="0 0 55 55" xmlns="http://www.w3.org/2000/svg">
      <circle cx="27.5" cy="27.5" r="24" fill="#6366f1" stroke="#ffffff" stroke-width="4"/>
      <path d="M20 38v-11h11v11h8v-14h5.5L27.5 13 10 27h5.5v11z" fill="white"/>
    </svg>
  `),
  iconSize: [55, 55],
  iconAnchor: [27.5, 55],
  popupAnchor: [0, -55],
});

const eventIconLarge = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="55" height="55" viewBox="0 0 55 55" xmlns="http://www.w3.org/2000/svg">
      <circle cx="27.5" cy="27.5" r="24" fill="#f59e0b" stroke="#ffffff" stroke-width="4"/>
      <path d="M27.5 36.5L35.5 41l-2.25-9.7L41 24.25l-9.9-.84L27.5 15 24.9 23.41 15 24.25l7.75 6.05L20.5 41z" fill="white"/>
    </svg>
  `),
  iconSize: [55, 55],
  iconAnchor: [27.5, 55],
  popupAnchor: [0, -55],
});

const activitiesIconLarge = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="55" height="55" viewBox="0 0 55 55" xmlns="http://www.w3.org/2000/svg">
      <circle cx="27.5" cy="27.5" r="24" fill="#0ea5e9" stroke="#ffffff" stroke-width="4"/>
      <path d="M37 18H18c-1.5 0-2.75 1.25-2.75 2.75v13.5c0 1.5 1.25 2.75 2.75 2.75h19c1.5 0 2.75-1.25 2.75-2.75v-13.5C39.75 19.25 38.5 18 37 18zm-9.5 2.75l1.6 3.45L33 25.22l-3 3L30.48 32L27.5 30.2L24.52 32l.7-3.7L22.25 25.22l3.9-.92L27.5 20.75z" fill="white"/>
    </svg>
  `),
  iconSize: [55, 55],
  iconAnchor: [27.5, 55],
  popupAnchor: [0, -55],
});

interface MapViewProps {
  events: Event[];
  selectedEvent?: Event | null;
  onMarkerClick?: (event: Event) => void;
  locationName?: string;
  location?: Location | null;
  onViewMore?: (event: Event) => void;
  highlightedEvent?: Event | null;
}

// Component to handle map center changes or bounds fitting
const ChangeMapCenter: React.FC<{ 
  center?: [number, number]; 
  bounds?: L.LatLngBoundsExpression;
  fitBounds?: boolean;
}> = ({ center, bounds, fitBounds }) => {
  const map = useMap();
  
  useEffect(() => {
    if (fitBounds && bounds) {
      console.log('Fitting map to bounds:', bounds);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    } else if (center) {
      console.log('Updating map center to:', center);
      map.setView(center, 13);
    }
  }, [center, bounds, fitBounds, map]);
  
  return null;
};

// Component for the popup content that can use hooks
const EventPopupContent: React.FC<{ event: Event; onMarkerClick?: (event: Event) => void; onViewMore?: (event: Event) => void }> = ({ event, onMarkerClick, onViewMore }) => {
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
  const [saveSnackbarOpen, setSaveSnackbarOpen] = useState(false);
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const isEventFavorite = isFavorite(event.uniqueId);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEventFavorite) {
      removeFromFavorites(event.uniqueId);
    } else {
      addToFavorites(event);
      setSaveSnackbarOpen(true);
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const shareData = {
      title: event.eventTitle,
      text: event.subhead || `Experience ${event.eventTitle}`,
      url: event.affiliateUrl,
    };

    // Check if device supports native sharing
    if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Desktop: Copy to clipboard
      try {
        await navigator.clipboard.writeText(event.affiliateUrl);
        setShareSnackbarOpen(true);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = event.affiliateUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShareSnackbarOpen(true);
      }
    }
  };

  return (
    <div style={{ minWidth: '250px', padding: '8px' }}>
      <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>
        {event.eventTitle}
      </div>
      
      <div style={{ marginBottom: '8px' }}>
        {'⭐'.repeat(Math.floor(event.starRating))} 
        <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
          ({event.starRating} stars)
        </span>
      </div>

      {event.category && (
        <div style={{ 
          display: 'inline-block',
          padding: '2px 8px',
          backgroundColor: event.category === 'hotel' || event.category === 'vacation_rentals' ? '#6366f1' : 
                         event.category === 'activities' ? '#0ea5e9' : '#f59e0b',
          color: 'white',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          marginBottom: '8px'
        }}>
          {event.category === 'activities' ? 'Activities' : 
           event.category === 'hotel' ? 'Hotel' : 
           event.category === 'vacation_rentals' ? 'Vacation Rentals' : 'Event'}
        </div>
      )}

      {event.subhead && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ 
            fontSize: '14px', 
            color: '#666', 
            fontStyle: 'italic',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical' as any,
            overflow: 'hidden',
            lineHeight: '1.4',
            marginBottom: '4px'
          }}>
            {event.subhead}
          </div>
          {onViewMore && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewMore(event);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#6366f1',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '0',
                textDecoration: 'underline'
              }}
            >
              View more
            </button>
          )}
        </div>
      )}
      
      {event.address?.street && (
        <div style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>
          📍 {event.address.street}<br />
          {event.address.city}, {event.address.state} {event.address.zip}
        </div>
      )}
      
      <div style={{ 
        display: 'flex',
        gap: '6px',
        justifyContent: 'center',
        marginTop: '12px'
      }}>
        <button 
          onClick={() => onMarkerClick && onMarkerClick(event)}
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            flex: 1
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
          }}
        >
          Book Now ⇗
        </button>
        <button 
          onClick={handleFavoriteClick}
          style={{
            background: isEventFavorite ? '#ef4444' : 'transparent',
            color: isEventFavorite ? 'white' : '#ef4444',
            border: isEventFavorite ? 'none' : '1px solid #ef4444',
            borderRadius: '8px',
            padding: '6px 8px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '3px'
          }}
          onMouseOver={(e) => {
            if (isEventFavorite) {
              e.currentTarget.style.background = '#dc2626';
            } else {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = isEventFavorite ? '#ef4444' : 'transparent';
          }}
        >
          {isEventFavorite ? 
            <Favorite sx={{ fontSize: '0.9rem' }} /> : 
            <FavoriteBorder sx={{ fontSize: '0.9rem' }} />
          }
          {isEventFavorite ? 'Saved' : 'Save'}
        </button>
        <button 
          onClick={handleShare}
          style={{
            background: 'transparent',
            color: '#6366f1',
            border: '1px solid #6366f1',
            borderRadius: '8px',
            padding: '6px 8px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '3px'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Share sx={{ fontSize: '0.9rem' }} />
          Share
        </button>
      </div>
      
      <Snackbar
        open={saveSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSaveSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSaveSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Saved to favorites!
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setShareSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShareSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </div>
  );
};

const MapView: React.FC<MapViewProps> = ({ events, selectedEvent, onMarkerClick, locationName, location, onViewMore, highlightedEvent }) => {
  // Helper function to get the appropriate icon based on category and highlight status
  const getEventIcon = (event: Event, isHighlighted: boolean) => {
    const isHotel = event.category === 'hotel' || event.category === 'vacation_rentals';
    const isActivity = event.category === 'activities';
    
    if (isHighlighted) {
      return isHotel ? hotelIconLarge : isActivity ? activitiesIconLarge : eventIconLarge;
    } else {
      return isHotel ? hotelIcon : isActivity ? activitiesIcon : eventIcon;
    }
  };
  // City center coordinates mapping (fallback)
  const cityCoordinates: { [key: string]: [number, number] } = {
    'New York': [40.7128, -74.0060],
    'New York City': [40.7128, -74.0060],
    'NYC': [40.7128, -74.0060],
    'San Francisco': [37.7749, -122.4194],
    'Los Angeles': [34.0522, -118.2437],
    'Chicago': [41.8781, -87.6298],
    'Miami': [25.7617, -80.1918],
    'Las Vegas': [36.1699, -115.1398],
    'Seattle': [47.6062, -122.3321],
    'Boston': [42.3601, -71.0589],
    'Philadelphia': [39.9526, -75.1652],
    'Washington DC': [38.9072, -77.0369],
    'Atlanta': [33.7490, -84.3880],
    'Denver': [39.7392, -104.9903],
    'Austin': [30.2672, -97.7431],
    'Portland': [45.5152, -122.6784],
    'Nashville': [36.1627, -86.7816],
    'New Orleans': [29.9511, -90.0715],
  };
  
  // Default center on San Francisco if no specific location found
  const defaultCenter: [number, number] = [37.7749, -122.4194];
  
  // Find center based on location coordinates first, then name mapping, then events
  const eventsWithCoords = events.filter(e => e.address?.latitude && e.address?.longitude);
  
  // Calculate bounds if we have multiple events and no specific location coordinates
  const shouldFitBounds = !location?.latitude && !location?.longitude && eventsWithCoords.length > 1;
  
  const bounds = useMemo(() => {
    if (shouldFitBounds && eventsWithCoords.length > 1) {
      const lats = eventsWithCoords.map(e => e.address!.latitude!);
      const lngs = eventsWithCoords.map(e => e.address!.longitude!);
      
      const southWest = L.latLng(Math.min(...lats), Math.min(...lngs));
      const northEast = L.latLng(Math.max(...lats), Math.max(...lngs));
      
      return L.latLngBounds(southWest, northEast);
    }
    return null;
  }, [eventsWithCoords, shouldFitBounds]);
  
  let center: [number, number];
  
  // Debug logging
  console.log('MapView Debug:', {
    location,
    locationName,
    hasLocationCoords: location?.latitude && location?.longitude,
    locationCoords: location ? [location.latitude, location.longitude] : null,
    shouldFitBounds,
    eventsCount: eventsWithCoords.length
  });
  
  if (shouldFitBounds && bounds) {
    // If no location coordinates and multiple events, we'll fit bounds instead
    // Use center of bounds for initial map center
    const boundsCenter = bounds.getCenter();
    center = [boundsCenter.lat, boundsCenter.lng];
    console.log('Will fit to bounds, initial center:', center);
  } else if (location?.latitude && location?.longitude) {
    // Use location's coordinates if available
    center = [location.latitude, location.longitude];
    console.log('Using location coordinates:', center);
  } else if (locationName && cityCoordinates[locationName]) {
    // Fallback to city name mapping
    center = cityCoordinates[locationName];
    console.log('Using city name mapping for:', locationName, center);
  } else if (eventsWithCoords.length > 0) {
    // Use first event with coordinates
    center = [eventsWithCoords[0].address!.latitude!, eventsWithCoords[0].address!.longitude!] as [number, number];
    console.log('Using first event coordinates:', center);
  } else {
    // Default to San Francisco
    center = defaultCenter;
    console.log('Using default center (San Francisco):', center);
  }

  return (
    <MapContainer
      key={`${center[0]}-${center[1]}`} // Force re-render when center changes
      center={center}
      zoom={13}
      style={{ height: '100%', width: '100%' }}
      className="map-container"
      touchZoom={true}
      dragging={true}
      scrollWheelZoom={true}
      doubleClickZoom={true}
      zoomControl={true}
    >
      <ChangeMapCenter 
        center={shouldFitBounds ? undefined : center} 
        bounds={shouldFitBounds && bounds ? bounds : undefined}
        fitBounds={shouldFitBounds}
      />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {eventsWithCoords.map((event) => (
        <Marker
          key={event.uniqueId}
          position={[event.address!.latitude!, event.address!.longitude!]}
          icon={getEventIcon(event, highlightedEvent?.uniqueId === event.uniqueId)}
          eventHandlers={{
            click: () => onMarkerClick && onMarkerClick(event),
          }}
        >
          <Tooltip 
            direction="auto" 
            offset={[0, -20]} 
            opacity={0.9}
            permanent={false}
            className="custom-tooltip"
            sticky={true}
          >
            <div style={{
              fontSize: '12px',
              fontWeight: '500',
              padding: '0',
              width: '180px',
              maxWidth: '180px',
              overflow: 'hidden'
            }}>
              <img
                src={event.imageUrl}
                alt={event.eventTitle}
                style={{
                  width: '100%',
                  height: '120px',
                  objectFit: 'cover',
                  borderRadius: '8px 8px 0 0',
                  display: 'block'
                }}
              />
              <div style={{ padding: '8px', textAlign: 'center', width: '100%', boxSizing: 'border-box' }}>
                <div style={{
                  fontWeight: '600',
                  marginBottom: '4px',
                  fontSize: '13px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  lineHeight: '1.3',
                  width: '100%'
                }}>
                  {event.eventTitle}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  {'⭐'.repeat(Math.floor(event.starRating))} ({event.starRating})
                </div>
                {event.price && (
                  <div style={{ fontSize: '12px', color: '#059669', fontWeight: '600', marginBottom: '4px' }}>
                    {event.price}
                  </div>
                )}
                {event.category && (
                  <div style={{ 
                    fontSize: '11px', 
                    textTransform: 'capitalize',
                    color: '#888'
                  }}>
                    {event.category === 'activities' ? 'Activities' : 
                     event.category === 'hotel' ? 'Hotel' : 
                     event.category === 'vacation_rentals' ? 'Vacation Rentals' : 'Event'}
                  </div>
                )}
              </div>
            </div>
          </Tooltip>
          
          <Popup>
            <EventPopupContent event={event} onMarkerClick={onMarkerClick} onViewMore={onViewMore} />
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default MapView;